/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 5th 2022, 10:40:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  useAPIFetchItem,
  UseAPIRequestArgs,
  useAPIMutation,
  UseAPIMutationArgs,
  APISERVICEPATHS,
  useAPIFetchList,
  UseFetchListArgs,
  useAPIForm,
} from "lib/api";
import { UUID4 } from "lib/types";
import { Organization, OrganizationValidationSchema, OrganizationListItem, Department } from "lib/schemas";
import { useUserContext } from "authentication/providers/UserContextProvider";
import { useGlobalScope } from "core/providers";

const publicOrganizationsAPIPath = APISERVICEPATHS.publicOrganizations;
const workerOrganizationsAPIPath = APISERVICEPATHS.workerOrganizations;
const toggleActivationAPIPath = APISERVICEPATHS.organizationToggleActivation;

export const useAPIMutateOrganization = (args: Omit<UseAPIMutationArgs<Organization>, "apiServicePath">) =>
  useAPIMutation<Organization>({
    apiServicePath: workerOrganizationsAPIPath,
    ...args,
  });

/**
 * The backend provides a seperate endpoint for activating an organization to provide extra security
 * and avoid mixing up this operation with other organization mutation operations
 **/
export const useAPIOrganizationToggleActivation = (args: Omit<UseAPIMutationArgs<Organization>, "apiServicePath">) =>
  useAPIMutation<Organization>({
    apiServicePath: toggleActivationAPIPath,
    ...args,
  });

export const useAPIFetchOrganizationsList = (
  args: Omit<UseFetchListArgs<OrganizationListItem>, "apiServicePath" | "filterByGlobalOrganization">
) =>
  useAPIFetchList<OrganizationListItem>({
    apiServicePath: workerOrganizationsAPIPath,
    filterByGlobalOrganization: false,
    ...args,
  });

interface useFetchOrganizationArgs extends Partial<Pick<UseAPIRequestArgs<Organization>, "makeRequestOnInitialMount">> {
  organizationId: UUID4;
  dependencyList?: any[];
}

export const useAPIFetchPublicOrganization = (args: useFetchOrganizationArgs) => {
  const { organizationId, dependencyList = [] } = args;

  const {
    apiItem: organization,
    isMakingAPIRequest: isFetchingOrganization,
    fetchItem,
    ...otherValues
  } = useAPIFetchItem<Organization>({
    apiServicePath: publicOrganizationsAPIPath,
    makeRequestOnInitialMount: false,
    filterByGlobalOrganization: false,
  });

  React.useEffect(() => {
    if (organizationId) fetchItem(organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, ...dependencyList]);

  return { organization, isFetchingOrganization, ...otherValues };
};

export const useAPIWorkerFetchOrganization = () => {
  const {
    apiItem: organization,
    isMakingAPIRequest: isFetchingOrganization,
    fetchItem,
    ...otherValues
  } = useAPIFetchItem<Organization>({
    apiServicePath: workerOrganizationsAPIPath,
    makeRequestOnInitialMount: false,
  });

  const { loggedInUser } = useUserContext();

  const fetchOrganization = (organizationId: Organization["id"]) => {
    if (!loggedInUser) return;

    fetchItem(organizationId);
  };

  return { organization, isFetchingOrganization, fetchOrganization, ...otherValues };
};

/**
 * Fetching a default organization from API. Used if no selected organization exists in
 * local state  and no selected Organization Id exists in local storage.
 */

export const useAPIFetchDefaultOrganization = () => {
  const {
    items: apiDefaultOrganizations,
    fetchList: fetchDefaultOrganization,
    isMakingAPIRequest: isFetchingDefaultOrganization,
    ...otherValues
  } = useAPIFetchList<Organization>({
    apiServicePath: workerOrganizationsAPIPath,
    makeRequestOnInitialMount: false,
    filterByGlobalOrganization: false,
    apiQueryParams: { default: true },
  });

  return {
    apiDefaultOrganization: apiDefaultOrganizations?.[0],
    fetchDefaultOrganization,
    isFetchingDefaultOrganization,
    ...otherValues,
  };
};

export const useAPIFetchPendingOrganization = (args: useFetchOrganizationArgs) => {
  const { organizationId, makeRequestOnInitialMount } = args;

  const {
    apiItem: organization,
    isMakingAPIRequest: isFetchingOrganization,
    fetchItem: fetchOrganization,
    ...otherValues
  } = useAPIFetchItem<Organization>({
    apiServicePath: workerOrganizationsAPIPath,
    makeRequestOnInitialMount,
    apiPathParams: [organizationId],
    apiQueryParams: { return_pending: true },
  });

  return { organization, isFetchingOrganization, fetchOrganization, ...otherValues };
};

interface UseOrganizationAPIFormOptions {
  organizationInitialFetchId?: Organization["id"];
  organization: Organization;
  setOrganization: (organization: Organization) => void;
}

export const useOrganizationAPIForm = ({
  organizationInitialFetchId = null,
  organization,
  setOrganization,
}: UseOrganizationAPIFormOptions) => {
  const {
    globalState: { selectedOrganization },
    updateGlobalOrganizationScope,
  } = useGlobalScope();

  const newDepartmentDefault: Department = {
    name: "",
    sequence: 0,
    organization: organization?.id,
    workers: [],
  };

  const updateOrganizationFromAPIResponse = (organizationFromAPIResponse: Organization) => {
    setOrganization(organizationFromAPIResponse);
    if (selectedOrganization?.id && selectedOrganization?.id === organizationFromAPIResponse?.id)
      updateGlobalOrganizationScope({ selectedOrganization: organizationFromAPIResponse });
  };

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: organization,
    apiFormDataValidationSchema: OrganizationValidationSchema,
    apiInitialFetchId: organizationInitialFetchId,
    apiServicePath: APISERVICEPATHS.workerOrganizations,
    setAPIFormResponse: updateOrganizationFromAPIResponse,
    primaryKey: "id",
    titleKey: "name",
  });

  const watched_organization_name = useAPIFormReturn.useFormReturn.watch("name");

  return {
    useAPIFormReturn,
    newDepartmentDefault,
    watched_organization_name,
  };
};
